import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import CustomCalendarHeader from './CustomCalendarHeader';
import useMountEffect from '../../hooks/useMountEffect';

require('./EventCalendar.less');
require('../../../node_modules/react-big-calendar/lib/css/react-big-calendar.css');
require('../../../node_modules/react-big-calendar/lib/addons/dragAndDrop/styles.css');

// Set Monday as the first day of the week
moment.locale('en', {
	week: {
		dow: 1, // Monday
	},
});

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar) as any;

/** Column slide duration. `behavior: 'smooth'` was too quick to follow. */
const SCROLL_ANIMATION_MS = 500;
/** Quiet period after a free scroll before it settles onto the nearest column. */
const SCROLL_SNAP_DELAY_MS = 140;

const getResourceColumnWidth = (container: HTMLElement): number => {
	const firstColumn = container.querySelector(
		'.rbc-time-view-resources .rbc-time-content > .rbc-day-slot'
	) as HTMLElement | null;
	return firstColumn ? firstColumn.offsetWidth : 0;
};

/** Eases `element` to `left` over SCROLL_ANIMATION_MS. Returns a cancel function. */
const animateScrollLeft = (element: HTMLElement, left: number, onDone: () => void) => {
	const startLeft = element.scrollLeft;
	const distance = left - startLeft;
	const startedAt = Date.now();
	let frame = requestAnimationFrame(function step() {
		const progress = Math.min(1, (Date.now() - startedAt) / SCROLL_ANIMATION_MS);
		element.scrollLeft = startLeft + distance * (1 - Math.pow(1 - progress, 3));
		if (progress < 1) {
			frame = requestAnimationFrame(step);
		} else {
			onDone();
		}
	});
	return () => cancelAnimationFrame(frame);
};

const EventCalendar: FC<any> = ({
	events,
	handleEventSelect,
	onSelectEvent,
	scrollToTime,
	scrollApiRef,
	onResourceScrollIndexChange,
	resourceColumnCount,
	...otherProps
}) => {
	const [currentTime, setCurrentTime] = useState(new Date());
	const containerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const interval = setInterval(() => {
			setCurrentTime(new Date());
		}, 60000);

		return () => clearInterval(interval);
	}, []);

	// handling scrolltotime.
	useEffect(() => {
		if (!containerRef.current || !scrollToTime) {
			return;
		}

		const content = containerRef.current.querySelector('.rbc-time-content') as HTMLElement | null;

		if (!content) {
			return;
		}

		const minutesSinceMidnight = scrollToTime.getHours() * 60 + scrollToTime.getMinutes();
		const totalMinutesInDay = 24 * 60;
		const ratio = minutesSinceMidnight / totalMinutesInDay;

		content.scrollTop = Math.min(
			content.scrollHeight * ratio,
			content.scrollHeight - content.clientHeight
		);
	}, [scrollToTime]);

	// The grid's scroll position is the source of truth for which resource column is in view.
	useMountEffect(() => {
		const container = containerRef.current;
		if (!container) {
			return undefined;
		}
		// Looked up per use: react-big-calendar replaces this node when the view changes.
		const getGrid = () => container.querySelector('.rbc-time-content') as HTMLElement | null;

		let cancelAnimation = null;
		let snapTimeout = null;

		const getColumnIndex = () => {
			const grid = getGrid();
			const width = getResourceColumnWidth(container);
			if (!grid || !width) {
				return 0;
			}
			// The last screenful is a half column short of a whole number of columns, since the grid
			// shows 4.5 of them at a time. Rounding the raw offset there sits exactly on the .5
			// boundary, so a sub-pixel scrollLeft reports one column short of the end and the caller's
			// "can I go right?" check never becomes false. At the end, report the end.
			const maxScrollLeft = grid.scrollWidth - grid.clientWidth;
			if (maxScrollLeft > 0 && grid.scrollLeft >= maxScrollLeft - 1) {
				return Math.ceil(maxScrollLeft / width);
			}
			return Math.round(grid.scrollLeft / width);
		};

		const reportColumnIndex = () => {
			if (onResourceScrollIndexChange) {
				onResourceScrollIndexChange(getColumnIndex());
			}
		};

		// Drops an animation in flight so a gesture or a command takes over immediately. Deliberately
		// leaves the pending snap alone: a gesture that ends against either end of the scroll range
		// moves nothing, so no further scroll event follows to reschedule it, and clearing it here
		// would discard the only report of where the gesture landed.
		const cancelSlideAnimation = () => {
			if (cancelAnimation) {
				cancelAnimation();
				cancelAnimation = null;
			}
		};

		const stopSliding = () => {
			cancelSlideAnimation();
			clearTimeout(snapTimeout);
		};

		const slideToColumn = (index) => {
			stopSliding();
			const grid = getGrid();
			if (!grid) {
				reportColumnIndex();
				return;
			}
			// Clamped, so a slide onto the last column settles at the end of the range instead of
			// animating against a wall the browser will clamp anyway.
			const maxScrollLeft = Math.max(0, grid.scrollWidth - grid.clientWidth);
			const target = Math.min(
				Math.max(0, index) * getResourceColumnWidth(container),
				maxScrollLeft
			);
			if (Math.abs(grid.scrollLeft - target) < 1) {
				reportColumnIndex();
				return;
			}
			cancelAnimation = animateScrollLeft(grid, target, () => {
				cancelAnimation = null;
				reportColumnIndex();
			});
		};

		// react-big-calendar keeps the header in step with the grid itself (TimeGrid's own onScroll), so
		// all this adds is settling a free scroll onto the nearest column, the way a button press does.
		// No React work here — a gesture must not trigger renders.
		const handleScroll = () => {
			if (cancelAnimation) {
				return;
			}
			clearTimeout(snapTimeout);
			snapTimeout = setTimeout(() => slideToColumn(getColumnIndex()), SCROLL_SNAP_DELAY_MS);
		};

		if (scrollApiRef) {
			scrollApiRef.current = {
				scrollByColumns: (delta) => slideToColumn(getColumnIndex() + delta),
				scrollToColumn: slideToColumn,
			};
		}

		container.addEventListener('scroll', handleScroll, { capture: true, passive: true });
		container.addEventListener('wheel', cancelSlideAnimation, { passive: true });
		container.addEventListener('touchstart', cancelSlideAnimation, { passive: true });
		return () => {
			stopSliding();
			container.removeEventListener('scroll', handleScroll, { capture: true } as any);
			container.removeEventListener('wheel', cancelSlideAnimation);
			container.removeEventListener('touchstart', cancelSlideAnimation);
			if (scrollApiRef) {
				scrollApiRef.current = null;
			}
		};
	});

	const calendarComponents = useMemo(
		() => ({
			header: CustomCalendarHeader,
			...otherProps.components,
		}),
		[otherProps.components]
	);

	// Stacking offsets come from stackingLayout as left/width percentages, which react-big-calendar
	// applies itself and which cannot push a card out of its column.
	const eventPropGetter = (event) => {
		const baseClass = `calEventCategory--${event.category}`;
		const statusClass = event.eventStatus ? `calEventStatus--${event.eventStatus}` : '';
		return { className: `${baseClass} ${statusClass}`.trim() };
	};

	const wrapperStyle = resourceColumnCount
		? ({ '--resource-columns': String(resourceColumnCount) } as React.CSSProperties)
		: {};

	return (
		<div className="eventCalendar__wrapper" style={wrapperStyle} ref={containerRef}>
			<DnDCalendar
				localizer={localizer}
				{...otherProps}
				events={events}
				startAccessor="start"
				endAccessor="end"
				step={otherProps.step !== undefined ? otherProps.step : 60}
				eventPropGetter={eventPropGetter}
				showMultiDayTimes
				onSelectEvent={onSelectEvent}
				now={currentTime}
				popup={true}
				popupOffset={{ x: 10, y: 10 }}
				components={calendarComponents}
				formats={{
					timeGutterFormat: (date, culture, localizer) => {
						return localizer.format(date, 'h:mm A', culture);
					},
					...otherProps.formats,
				}}
				timeslots={otherProps.timeslots !== undefined ? otherProps.timeslots : 1}
				draggableAccessor={otherProps.draggableAccessor}
				onEventDrop={otherProps.onEventDrop}
			/>
		</div>
	);
};

export default EventCalendar;
